import React, { useEffect, useState } from 'react';
import {
  getTenants,
  TenantStats,
  connectWhatsAppCredential,
  WhatsAppNumberConflictError,
  saveWhatsAppAppConfig,
} from '../../../api/backoffice';

/**
 * Superadmin connect-form (WhatsApp P2c-3). The operator pastes a tenant's WABA credential and it is
 * POSTed to the store endpoint (POST /api/backoffice/whatsapp/credentials), which encrypts the token at
 * rest. This form NEVER echoes/logs the token, never puts it in the URL, and shows only the non-secret
 * confirmation on success. A cross-tenant number reclaim (409) is surfaced as a clear error.
 *
 * The app-level WHATSAPP_APP_SECRET is a single per-env secret delivered via env_file (DevOps-owned) —
 * NOT a per-tenant value — so it is deliberately NOT a field here.
 */
const WhatsAppConnectPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantStats[]>([]);
  const [accountId, setAccountId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [displayNumber, setDisplayNumber] = useState('');
  const [status, setStatus] = useState<{ kind: 'idle' | 'saving' | 'success' | 'error'; message?: string }>({ kind: 'idle' });

  useEffect(() => {
    getTenants().then(setTenants).catch(() => {/* tenants list optional; the field still accepts an id */});
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ kind: 'saving' });
    try {
      const r = await connectWhatsAppCredential({
        accountId,
        phoneNumberId,
        wabaId,
        accessToken,
        displayNumber: displayNumber || undefined,
      });
      // Never render the token — only the non-secret identifiers the endpoint returns.
      setStatus({ kind: 'success', message: `Connected number ${r.phoneNumberId} to tenant ${r.accountId}.` });
      setAccessToken(''); // drop the secret from state after a successful connect
    } catch (err) {
      if (err instanceof WhatsAppNumberConflictError) {
        setStatus({ kind: 'error', message: `${err.message} (this number is already connected to a different tenant).` });
      } else {
        setStatus({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to connect WhatsApp.' });
      }
    }
  };

  // ── App-level config (App-Secret + verify token) — the delivery path for a shell-less operator. ──
  const [appSecret, setAppSecret] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [appStatus, setAppStatus] = useState<{ kind: 'idle' | 'saving' | 'success' | 'error'; message?: string }>({ kind: 'idle' });

  const submitAppConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setAppStatus({ kind: 'saving' });
    try {
      await saveWhatsAppAppConfig({ appSecret, verifyToken });
      setAppStatus({ kind: 'success', message: 'App config saved (encrypted).' }); // never echo the secrets
      setAppSecret('');
      setVerifyToken('');
    } catch (err) {
      setAppStatus({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to save app config.' });
    }
  };

  const inputClass = 'mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm';

  return (
    <div data-testid="whatsapp-connect-page" className="p-6 max-w-lg space-y-8">
      <section data-testid="whatsapp-app-config">
        <h1 className="text-xl font-semibold mb-1">WhatsApp App Config</h1>
        <p className="text-sm text-gray-500 mb-4">The app-level App Secret (webhook signature) and verify token. Encrypted at rest; never shown again.</p>
        <form onSubmit={submitAppConfig} className="space-y-4">
          <label className="block text-sm font-medium">
            App Secret
            <input data-testid="app-secret" type="password" required autoComplete="off" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} className={inputClass} />
          </label>
          <label className="block text-sm font-medium">
            Verify Token
            <input data-testid="verify-token" type="password" required autoComplete="off" value={verifyToken} onChange={(e) => setVerifyToken(e.target.value)} className={inputClass} />
          </label>
          <button data-testid="app-config-submit" type="submit" disabled={appStatus.kind === 'saving'} className="rounded bg-green-600 px-4 py-2 text-white text-sm disabled:opacity-50">
            {appStatus.kind === 'saving' ? 'Saving…' : 'Save App Config'}
          </button>
        </form>
        {appStatus.kind === 'success' && <p data-testid="app-config-success" className="mt-3 text-sm text-green-700">{appStatus.message}</p>}
        {appStatus.kind === 'error' && <p data-testid="app-config-error" className="mt-3 text-sm text-red-600">{appStatus.message}</p>}
      </section>

      <h1 className="text-xl font-semibold mb-1">Connect WhatsApp</h1>
      <p className="text-sm text-gray-500 mb-4">Link a tenant's WhatsApp Business number. The access token is encrypted at rest and never shown again.</p>
      <form onSubmit={submit} className="space-y-4">
        <label className="block text-sm font-medium">
          Tenant
          <select data-testid="wa-account" required value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputClass}>
            <option value="">Select a tenant…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.id})</option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium">
          Phone Number ID
          <input data-testid="wa-phoneNumberId" required value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)} className={inputClass} />
        </label>
        <label className="block text-sm font-medium">
          WABA ID
          <input data-testid="wa-wabaId" required value={wabaId} onChange={(e) => setWabaId(e.target.value)} className={inputClass} />
        </label>
        <label className="block text-sm font-medium">
          Access Token
          <input data-testid="wa-accessToken" type="password" required autoComplete="off" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} className={inputClass} />
        </label>
        <label className="block text-sm font-medium">
          Display Number <span className="text-gray-400">(optional)</span>
          <input data-testid="wa-displayNumber" value={displayNumber} onChange={(e) => setDisplayNumber(e.target.value)} className={inputClass} />
        </label>
        <button data-testid="wa-submit" type="submit" disabled={status.kind === 'saving'} className="rounded bg-green-600 px-4 py-2 text-white text-sm disabled:opacity-50">
          {status.kind === 'saving' ? 'Connecting…' : 'Connect'}
        </button>
      </form>
      {status.kind === 'success' && <p data-testid="wa-success" className="mt-4 text-sm text-green-700">{status.message}</p>}
      {status.kind === 'error' && <p data-testid="wa-error" className="mt-4 text-sm text-red-600">{status.message}</p>}
    </div>
  );
};

export default WhatsAppConnectPage;
